import React, { useEffect, useMemo, useState } from 'react';
import {
  MovaticFieldGroupText,
  MovaticFieldGroupAsyncSelect,
} from '../../components/Redesing/movatic-field-group';
import CustomToast from '../../components/Redesing/custom-toast';
import FileUpload from '../../components/Redesing/file-upload';

import { Option } from '../../ts/interface/components/table.interface';
import CardView from '../../components/Redesing/card-view';
import PageContainer from '../../components/Redesing/page-container';
import useOrganizations from '../../hooks/system/query/useOrganizations';
import NoFilePlaceHolder from '../../components/Redesing/no-file-place-holder';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { postOrganization, postOrganizationLogo } from '../../api/organization';
import { history } from '../../utils';
import { useOrganizationStore } from '../../store/organizationStore';
import BigButton from '../../components/Redesing/big-button';
import { Stack, Typography } from '@mui/material';

function AddOrganization() {
  const initialState = {
    name: '',
    parentOrganization: null as Option | null,
    parentOrganizationValidationResult: null,
    parentOrganizationValidationHelp: '',
    nameValidationResult: null,
    nameValidationHelp: '',
    file: [],
  };

  const toBase64 = (file: Blob): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const formik = useFormik({
    initialValues: initialState,
    validationSchema: Yup.object({
      name: Yup.string().required('Organization Name is required'),
    }),
    onSubmit: async (values, helpers): Promise<void> => {
      try {
        helpers.setSubmitting(true);

        let imageJson;

        if (logoFiles.length > 0) {
          const base64Image = await toBase64(logoFiles[0] as Blob);
          const imageResponse = await postOrganizationLogo(base64Image);
          imageJson = (await imageResponse.json()) as any;
        }

        const response = await postOrganization({
          name: values.name,
          admin_config: {
            details: { last_access: new Date().toISOString() },
          },
          config: {
            logo_id: imageJson?.message?.gid,
            details: { last_access: new Date().toISOString() },
          },
          parent_org: {
            id: values.parentOrganization?.value,
            config: {
              details: { last_access: new Date().toISOString() },
            },
          },
        });

        // if organization is created successfully, redirect to the organization page
        CustomToast({
          type: 'success',
          message: 'Organization created successfully',
          duration: 5000,
        });
        setTimeout(() => {
          if (response.status === 201) {
            // set new organization on zustand
            const setOrganizationInfo =
              useOrganizationStore.getState().setOrganizationInfo;
            setOrganizationInfo({
              id: response.message.child_organization_id,
              name: values.name,
              access: -1,
            });

            history.push(
              '/dashboard?organizationId=' + response.message.child_organization_id
            );
          }
        }, 1000);
      } catch (error) {
        CustomToast({
          type: 'error',
          message: `Error creating organization ${error}`,
          duration: 5000,
        });
      }
    },
  });

  const [logoFiles, setLogoFiles] = useState<
    ((Blob | MediaSource) & { preview: string })[]
  >([]);
  const handleFileUpload = (files: Blob[] | MediaSource[], rejected: string) => {
    if (rejected.length > 0) {
      formik.errors.file = 'That photo is too large';
      return;
    }

    const images = files.map((file) =>
      Object.assign(file, {
        preview: URL.createObjectURL(file),
      })
    );

    const image = new Image();
    image.src = images[0].preview;
    image.onload = () => {
      if (image.width !== 200 || image.height !== 200) {
        CustomToast({
          type: 'error',
          message: 'Your image is not 200px by 200px, please reformat and try again',
          duration: 5000,
        });
        formik.errors.file =
          'Your image is not 200px by 200px, please reformat and try again';
        formik.setFieldValue('file', []);
        setLogoFiles([]);
      } else {
        formik.setFieldValue('file', images);
        setLogoFiles(images);
      }
    };
  };

  const [openAsyncSelect, setOpenAsyncSelect] = useState(false);

  const { data, isLoading, refetch: refetchOrganizations } = useOrganizations();

  const parentOrganizations = useMemo(() => data?.data || [], [data]);

  useEffect(() => {
    if (openAsyncSelect) {
      refetchOrganizations();
    }
  }, [openAsyncSelect, refetchOrganizations]);

  const parentOrganizationOptions = [
    ...parentOrganizations.map((parent: { name: string; id: string }) => ({
      label: parent.name,
      value: parent.id,
    })),
  ] as Option[];

  return (
    <PageContainer>
      <CardView
        title={'Add Organization'}
        content={
          <form onSubmit={formik.handleSubmit}>
            <Stack px={2} py={2} pb={2} gap={2}>
              <MovaticFieldGroupText
                id="name"
                name="name"
                label="Organization Name"
                description="Required"
                hasError={!!(formik.touched.name && formik.errors.name)}
                errorMessage={formik.errors.nameValidationHelp}
                value={formik.values.name}
                onChange={formik.handleChange}
              />

              <MovaticFieldGroupAsyncSelect
                isLoading={isLoading}
                id={'parent_organization'}
                label="Parent Organization"
                name={'parent_organization'}
                options={parentOrganizationOptions}
                value={formik.values.parentOrganization}
                showClearButton
                onChange={(event: any, newValue: any) =>
                  formik.setFieldValue('parentOrganization', newValue)
                }
                errorMessage={formik.errors.parentOrganizationValidationHelp}
                hasError={
                  !!(
                    formik.touched.parentOrganization && formik.errors.parentOrganization
                  )
                }
                open={openAsyncSelect}
                onOpen={() => {
                  setOpenAsyncSelect(true);
                }}
                onClose={() => {
                  setOpenAsyncSelect(false);
                }}
                isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
                getOptionText={(option: any) => option.label}
                handleSearch={() => {}}
              />
              {/** add input for logo */}
              <Stack direction={'row'} spacing={2} justifyContent={'space-between'}>
                <Stack alignItems={'flex-start'}>
                  <Typography variant={'subtitle1'} px={1}>
                    Logo Preview
                  </Typography>
                  {logoFiles.length > 0 ? (
                    <img alt="" className="logoRadius" src={logoFiles[0].preview} />
                  ) : (
                    <NoFilePlaceHolder />
                  )}
                </Stack>

                <Stack sx={{ width: '58%' }}>
                  <Typography variant={'subtitle1'}>Upload Organization Logo</Typography>
                  <FileUpload
                    caption={
                      'The file must be a .png, 200px wide, 200px high, and less than 1MB'
                    }
                    onDrop={handleFileUpload}
                  />
                </Stack>
              </Stack>

              <BigButton
                description={formik.isSubmitting ? 'Creating...' : 'Create Organization'}
                onClick={formik.handleSubmit}
              />
            </Stack>
          </form>
        }
      />
    </PageContainer>
  );
}

export default AddOrganization;
